import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { CButton, CCloseButton, CCol, CModal, CModalBody, CRow } from '@coreui/react-pro'

import { ReactComponent as DefaultTypeImage } from '@assets/images/no-sessions-to-review.svg'
import { ReactComponent as FormsTypeImage } from '@assets/images/self-care-plan-created.svg'
import { ReactComponent as SubscriptionUpgraded } from '@assets/images/subscription-upgraded.svg'
import { ReactComponent as GenerationsTypeImage } from '@assets/images/women-meditating.svg'
import SubscriptionSwitch from '@components/atoms/SubscriptionSwitch'
import { azureX, black } from '@constants/colors'
import {
  bookADemoLink,
  hubSpotMonthlyPaymentLinkEUR,
  hubSpotMonthlyPaymentLinkGBP,
  hubSpotMonthlyPaymentLinkUSD,
  hubSpotYearlyPaymentLink,
} from '@constants/links'
import { SubscriptionPlan, useMeQuery, useOrganizationQuery } from '@graphQLGenerated'
import { IUpgradeNowModal } from '@interfaces/components/organisms/UpgradeNowModal'
import { analytics, SegmentEvent } from '@utils/segment'

import './styles.scss'

const UpgradeNowModal: React.FC<IUpgradeNowModal> = ({
  isVisible,
  onClose,
  isEndOfTrial,
  source,
  onClickLogout,
  type,
}) => {
  const { data: meData, client } = useMeQuery()
  const { data: organizationData, stopPolling, startPolling } = useOrganizationQuery()
  const [upgradePressed, setUpgradePressed] = useState(false)

  useEffect(() => {
    if (!isVisible) {
      setUpgradePressed(false)
    } else {
      analytics.track(SegmentEvent.UPGRADE_NOW_MODAL_SHOWN, {
        source,
      })
    }
  }, [isVisible, source])

  useEffect(() => {
    if (isVisible && upgradePressed) {
      startPolling(2000)
    }
  }, [isVisible, startPolling, upgradePressed])

  const isSubscribed = useMemo(
    () =>
      organizationData?.me?.organization?.subscriptionPlan === SubscriptionPlan.Team ||
      organizationData?.me?.organization?.subscriptionPlan === SubscriptionPlan.Standard ||
      organizationData?.me?.organization?.subscriptionPlan === SubscriptionPlan.OneTimePayment,
    [organizationData?.me?.organization?.subscriptionPlan],
  )

  useEffect(() => {
    if (isSubscribed) {
      stopPolling()
      client.refetchQueries({
        include: ['Me'],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, isSubscribed])

  const metaData = useMemo(() => {
    if (isEndOfTrial) {
      return {
        closeButton: null,
      }
    }
    return {
      closeButton: (
        <div className="align-self-end">
          <CCloseButton onClick={onClose} />
        </div>
      ),
    }
  }, [isEndOfTrial, onClose])

  const headerMetaData = useMemo(() => {
    if (type === 'forms') {
      return {
        image: <FormsTypeImage className="w-100" height={200} />,
        title: 'Get unlimited sends via email and SMS with our PRO Plan!',
      }
    }
    if (type === 'generations') {
      return {
        image: <GenerationsTypeImage className="w-100" height={200} />,
        title: 'Generate unlimited resources as fast as ever with our PRO Plan!',
      }
    }
    if (type === 'transcripts') {
      return {
        image: <DefaultTypeImage className="w-100" height={200} />,
        title: 'Do more of what you love and leave the notes to us!',
      }
    }
    return {
      image: <DefaultTypeImage className="w-100" height={200} />,
      title: 'Do more of what you love and leave the rest to us!',
    }
  }, [type])

  const getPaymentLink = useCallback(
    (isYearly: boolean) => {
      if (isYearly) {
        return hubSpotYearlyPaymentLink
      }
      if (meData?.me?.countryCode === 'GB') {
        return hubSpotMonthlyPaymentLinkGBP
      }
      if (meData?.me?.eu) {
        return hubSpotMonthlyPaymentLinkEUR
      }
      return hubSpotMonthlyPaymentLinkUSD
    },
    [meData?.me?.countryCode, meData?.me?.eu],
  )

  const modalContent = useMemo(() => {
    if (isSubscribed) {
      return (
        <>
          <h5 className="text-center">Thank you!</h5>
          <div className="subtitle-font mb-2">Your payment is confirmed!</div>
          <div className="caption-font">Current Plan: Standard Plan</div>
          <SubscriptionUpgraded className="my-4" />
          <CButton
            className="mb-5"
            onClick={() => {
              if (isEndOfTrial) {
                window.location.reload()
              } else {
                onClose()
              }
            }}
          >
            OK, thank you!
          </CButton>
        </>
      )
    }
    return (
      <>
        <CCol sm={10} xs={12}>
          <CRow className="justify-content-center align-items-center mb-3">
            <CCol sm={4}>{headerMetaData.image}</CCol>
            <CCol>
              <div className="h5-font">{headerMetaData.title}</div>
              <div className="body-large-font">
                Want to save time and conquer admin work with automation? You’re ready for Pro!
              </div>
            </CCol>
          </CRow>
        </CCol>

        <CCol xs={12}>
          <SubscriptionSwitch
            hideImage
            showHeader={false}
            showDiscountCodeCheck={false}
            className="mb-4"
            buttonText="Upgrade Plan"
            showFreePlan={
              organizationData?.me?.organization?.subscriptionPlan === SubscriptionPlan.FreePlan
            }
            onSubmit={({ isYearly }) => {
              const paymentLink = getPaymentLink(isYearly)
              window.open(
                `${paymentLink}&email=${meData?.me?.organization?.owner?.email}`,
                '_blank',
              )
              setUpgradePressed(true)
              analytics.track(SegmentEvent.UPGRADE_PLAN_BUTTON_CLICKED, {
                subscriptionPeriod: isYearly ? 'yearly' : 'monthly',
                source,
              })
            }}
            isLoading={upgradePressed}
          />
        </CCol>
        <div className="UNMSeparator" />
        <h4>📞</h4>
        <div className="text-center subheader-font">
          Do you want to integrate uMore in your organization?
        </div>
        <div className="text-center body-small-font">
          We can offer you a tailored solution for the specific needs of your business.
        </div>
        <CButton
          color="primary"
          onClick={() => {
            window.open(bookADemoLink, '_blank')
            analytics.track(SegmentEvent.BOOK_A_DEMO_BUTTON_CLICKED, {
              source: 'Upgrade Now Modal',
            })
          }}
          className="mt-3"
          variant="outline"
          style={{ color: black }}
        >
          Book a call with us
        </CButton>
        {onClickLogout ? (
          <CButton color="secondary" onClick={onClickLogout} className="mt-3">
            Log out
          </CButton>
        ) : null}
      </>
    )
  }, [
    isSubscribed,
    headerMetaData.image,
    headerMetaData.title,
    organizationData?.me?.organization?.subscriptionPlan,
    upgradePressed,
    onClickLogout,
    isEndOfTrial,
    onClose,
    getPaymentLink,
    meData?.me?.organization?.owner?.email,
    source,
  ])

  return (
    <CModal visible={isVisible} onClose={onClose} alignment="center" size="lg" scrollable>
      <CModalBody className="d-flex column" style={{ backgroundColor: azureX }}>
        {metaData.closeButton}
        <CCol
          xs={12}
          className="d-flex column align-items-center justify-content-center align-self-center"
        >
          {modalContent}
        </CCol>
      </CModalBody>
    </CModal>
  )
}

export default UpgradeNowModal
